// Shared helpers for calculating when the next check of a tool/category is due.
// The logic mirrors the overdue/upcoming calculation used on the Home dashboard.

export function addInterval(date, interval, unit) {
  const d = new Date(date);
  const u = (unit || "").toLowerCase();
  if (u === "month" || u === "monat" || u === "monate" || u === "monat(e)") {
    d.setMonth(d.getMonth() + interval);
  } else if (u === "year" || u === "jahr" || u === "jahre") {
    d.setFullYear(d.getFullYear() + interval);
  } else if (u === "week" || u === "woche" || u === "wochen") {
    d.setDate(d.getDate() + interval * 7);
  } else if (u === "day" || u === "tag" || u === "tage") {
    d.setDate(d.getDate() + interval);
  } else {
    // Default to months if unit is missing or unknown
    d.setMonth(d.getMonth() + interval);
  }
  return d;
}

// Calculate the next due date for a single tool + category, or null if it
// cannot be determined (no interval and no purchase date).
export function getNextDueDate(tool, category, checks) {
  const interval = Number(category?.interval) || 0;
  const unit = category?.unit || "";

  const catChecks = (checks || []).filter(
    (check) =>
      check.ToolId === tool.id &&
      (check.categoryId === category.id || check.CategoryId === category.id) &&
      check.isValid !== false &&
      check.finished !== false
  );

  let lastCheckDate = null;
  if (catChecks.length > 0) {
    const latest = catChecks.reduce((a, b) =>
      new Date(a.checkdate) > new Date(b.checkdate) ? a : b
    );
    lastCheckDate = latest.checkdate ? new Date(latest.checkdate) : null;
  }

  if (lastCheckDate && interval > 0) {
    return { nextDueDate: addInterval(lastCheckDate, interval, unit), hasCheck: true };
  }
  if (tool.dateofpurchase && interval > 0) {
    return { nextDueDate: addInterval(new Date(tool.dateofpurchase), interval, unit), hasCheck: false };
  }
  return { nextDueDate: null, hasCheck: catChecks.length > 0 };
}

// Find the tool of a given tools group + category whose check is due next.
// Overdue tools (never checked, or past the due date) are preferred and
// returned sorted so the most overdue one comes first. Tools that already
// have a check in progress for this category are skipped.
// Returns { tool, nextDueDate, isOverdue } or null when nothing is due.
export function findNextDueTool({ tools, checks, toolsGroupId, category, excludeToolId }) {
  if (!category || !Array.isArray(tools)) return null;

  const today = new Date();
  const candidates = [];

  tools.forEach((tool) => {
    const groupId = tool.ToolsGroup?.id ?? tool.ToolsGroupId;
    if (groupId !== toolsGroupId) return;
    if (tool.id === excludeToolId) return;
    if (tool.isOperational === false || tool.isDiscarded === true) return;

    // Skip tools that already have an unfinished check for this category
    const hasInProgress = (checks || []).some(
      (check) =>
        check.ToolId === tool.id &&
        (check.categoryId === category.id || check.CategoryId === category.id) &&
        check.finished === false
    );
    if (hasInProgress) return;

    const { nextDueDate, hasCheck } = getNextDueDate(tool, category, checks);
    const isOverdue = !hasCheck || (nextDueDate && nextDueDate < today);
    if (!isOverdue) return;

    candidates.push({ tool, nextDueDate, isOverdue: true });
  });

  candidates.sort((a, b) => {
    if (!a.nextDueDate && !b.nextDueDate) return 0;
    if (!a.nextDueDate) return -1;
    if (!b.nextDueDate) return 1;
    return new Date(a.nextDueDate) - new Date(b.nextDueDate);
  });

  return candidates[0] || null;
}

// Find another check category of the same tool that is currently overdue,
// excluding the given category. Returns { category, nextDueDate, isOverdue }
// with the most overdue category first, or null when none is due.
export function findNextDueCategoryForTool({ tool, checks, categories, excludeCategoryId }) {
  if (!tool || !Array.isArray(categories)) return null;
  if (tool.isOperational === false || tool.isDiscarded === true) return null;

  const today = new Date();
  const candidates = [];

  categories.forEach((category) => {
    if (category.id === excludeCategoryId) return;

    // Skip categories that already have a check in progress for this tool
    const hasInProgress = (checks || []).some(
      (check) =>
        check.ToolId === tool.id &&
        (check.categoryId === category.id || check.CategoryId === category.id) &&
        check.finished === false
    );
    if (hasInProgress) return;

    const { nextDueDate, hasCheck } = getNextDueDate(tool, category, checks);
    const isOverdue = !hasCheck || (nextDueDate && nextDueDate < today);
    if (!isOverdue) return;

    candidates.push({ category, nextDueDate, isOverdue: true });
  });

  candidates.sort((a, b) => {
    if (!a.nextDueDate && !b.nextDueDate) return 0;
    if (!a.nextDueDate) return -1;
    if (!b.nextDueDate) return 1;
    return new Date(a.nextDueDate) - new Date(b.nextDueDate);
  });

  return candidates[0] || null;
}
